import { useState, useEffect, useContext, createContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  INFERENCE_PLUS: 'inference_plus',
  INFERENCE: 'inference',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.USER]: 'User',
  [ROLES.INFERENCE_PLUS]: 'Inference + own compute',
  [ROLES.INFERENCE]: 'Inference only',
};

// Mirrors resolveRole() in workers/src/auth/roles.js: a user object cached from
// a session that predates the role column carries only isAdmin.
export const resolveRole = (user) => {
  if (!user) return null;
  if (Object.values(ROLES).includes(user.role)) return user.role;
  return user.isAdmin ? ROLES.ADMIN : ROLES.USER;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = await authService.getUser();
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          authService.clearAuth();
          setIsAuthenticated(false);
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const signin = async (email, password) => {
    try {
      const result = await authService.signin(email, password);
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
      }
      return result;
    } catch (error) {
      console.error('Signin error:', error);
      return { success: false, error: 'Signin failed' };
    }
  };

  const signup = async (userData) => {
    try {
      const result = await authService.signup(userData);
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
      }
      return result;
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Signup failed' };
    }
  };

  const signout = async () => {
    try {
      await authService.signout();
    } catch (error) {
      console.error('Signout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshUser = async () => {
    if (authService.isAuthenticated()) {
      const userData = await authService.getUser();
      if (userData) {
        setUser(userData);
        return userData;
      }
    }
    return null;
  };

  const role = resolveRole(user);

  const value = {
    user,
    role,
    isAdmin: role === ROLES.ADMIN,
    // Gates the UI only. The backend enforces the same restriction in
    // workers/src/auth/roles.js, so hiding a control is convenience, not
    // security.
    isInferenceOnly: role === ROLES.INFERENCE,
    loading,
    isAuthenticated,
    signin,
    signup,
    signout,
    refreshUser,
    initializeAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};